import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { TenantConfig } from '../types/tenant';
import { getRuntimeConfig } from '../config/runtimeConfig';

interface TenantStore {
  // State
  config: TenantConfig | null;
  idToken: string | null;
  isConfigured: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  configure: (idToken: string, forceRefresh?: boolean) => Promise<void>;
  refreshConfig: () => Promise<void>;
  restoreToken: (idToken: string) => Promise<void>;
  reset: () => void;
  getBaseUrl: () => string | null;
  getChatBackendUrl: () => string | null;
  getIdToken: () => string | null;
}

const getTenantManagerUrl = () => getRuntimeConfig().TENANT_MANAGER_URL;

export const useTenantStore = create<TenantStore>()(
  persist(
    (set, get) => ({
  // Initial state
  config: null,
  idToken: null,
  isConfigured: false,
  isLoading: false,
  error: null,

  // Configure tenant with ID token (always fetch fresh config)
  configure: async (idToken: string, forceRefresh: boolean = false) => {
    set({ isLoading: true, error: null });

    try {
      const tenantManagerUrl = getTenantManagerUrl();

      console.log("=== APPEL API TENANT-MANAGER ===");
      console.log("URL:", tenantManagerUrl);
      console.log("Force refresh:", forceRefresh);

      const response = await fetch(tenantManagerUrl, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${idToken}`,
          "Content-Type": "application/json",
          // Forcer le rechargement en évitant le cache navigateur
          "Cache-Control": "no-cache, no-store, must-revalidate",
          "Pragma": "no-cache"
        }
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP lors de l'appel tenant-manager: ${response.status} - ${response.statusText}`);
      }

      const config: TenantConfig = await response.json();

      if (!config.baseUrl) {
        throw new Error("baseUrl manquante dans la réponse du tenant-manager");
      }

      if (!config.chatBackendUrl) {
        throw new Error("chatBackendUrl manquante dans la réponse du tenant-manager");
      }

      console.log("=== CONFIGURATION TENANT RÉCUPÉRÉE (FRESH) ===", config);

      set({
        config,
        idToken,
        isConfigured: true,
        isLoading: false,
        error: null
      });
    } catch (error) {
      console.error("Erreur lors de la configuration tenant:", error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';

      set({
        config: null,
        idToken: null,
        isConfigured: false,
        isLoading: false,
        error: `Impossible de récupérer la configuration tenant: ${errorMessage}`
      });

      throw error;
    }
  },

  // Refresh config with current token (always fetch fresh)
  refreshConfig: async () => {
    const { idToken } = get();
    if (!idToken) {
      throw new Error('No token available for config refresh');
    }

    console.log("=== REFRESH CONFIG DÉCLENCHÉ ===");
    await get().configure(idToken, true);
  },

  // Restore token from MSAL and always refresh config
  restoreToken: async (idToken: string) => {
    set({ idToken });

    // Toujours rafraîchir la config à la restauration du token
    console.log("=== TOKEN RESTAURÉ, REFRESH CONFIG ===");
    try {
      await get().configure(idToken, true);
    } catch (error) {
      console.error("Erreur lors du refresh de config après restauration token:", error);
      // Ne pas faire échouer la restauration du token pour autant
    }
  },

  // Reset store state
  reset: () => {
    set({
      config: null,
      idToken: null,
      isConfigured: false,
      isLoading: false,
      error: null
    });
  },

  // Get base URL (can be used from services)
  getBaseUrl: () => {
    const { config } = get();
    return config?.baseUrl || null;
  },

  // Get chat backend URL (can be used from services)
  getChatBackendUrl: () => {
    const { config } = get();
    return config?.chatBackendUrl || null;
  },

  // Get ID token (can be used from services)
  getIdToken: () => {
    const { idToken } = get();
    return idToken;
  }
}),
{
  name: 'tenant-store',
  // Persister seulement l'état de configuration, pas la config elle-même
  partialize: (state) => ({
    isConfigured: state.isConfigured // Garder l'info que l'utilisateur s'est déjà connecté
    // config et idToken ne sont pas persistés - seront rafraîchis à chaque démarrage
  })
}
));